import { useTranslation } from 'react-i18next';

/**
 * Partner strip — spec §7.1. A trust bar: "Follow" + socials on the left, travel-partner
 * wordmarks on the right. Social URLs and the partner list come from Settings (§6.18) in M4;
 * until then socials are aria-labelled placeholders and partners are text wordmarks (no
 * brand-logo assets were supplied — §20.2 default).
 */
const PARTNERS = ['trivago', 'Tripadvisor', 'Booking.com', 'Expedia'];

export function PartnerStrip() {
  const { t } = useTranslation();

  return (
    <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center gap-6 border-b border-line pb-6 sm:flex-row sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-content">{t('home.follow')}</span>
          <div className="flex gap-2">
            {SOCIALS.map(({ key, path }) => (
              <button
                key={key}
                type="button"
                aria-label={t(`home.social_${key}`)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-pill border border-line text-content-muted transition-colors hover:bg-content/5 hover:text-content"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                  <path d={path} />
                </svg>
              </button>
            ))}
          </div>
        </div>

        <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {PARTNERS.map((name) => (
            <li key={name} className="text-lg font-semibold text-content-muted/70">
              {name}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Minimal single-path glyphs — enough to read as the brand at 16px without an icon dep. */
const SOCIALS: { key: string; path: string }[] = [
  { key: 'x', path: 'M18.9 2H22l-7.5 8.6L23.3 22h-6.9l-5.4-7-6.2 7H1.7l8-9.2L.9 2h7l4.9 6.5L18.9 2Zm-1.2 18h1.9L7.1 4H5.1l12.6 16Z' },
  {
    key: 'facebook',
    path: 'M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.7l-.4 2.9h-2.3v7A10 10 0 0 0 22 12Z',
  },
  {
    key: 'instagram',
    path: 'M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2Zm0 3.2A6.6 6.6 0 1 0 18.6 12 6.6 6.6 0 0 0 12 5.4Zm0 10.9A4.3 4.3 0 1 1 16.3 12 4.3 4.3 0 0 1 12 16.3Zm6.8-11.1a1.5 1.5 0 1 1-1.5-1.5 1.5 1.5 0 0 1 1.5 1.5Z',
  },
  {
    key: 'linkedin',
    path: 'M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2ZM8 19H5V9h3v10Zm-1.5-11.3a1.7 1.7 0 1 1 1.7-1.7 1.7 1.7 0 0 1-1.7 1.7ZM19 19h-3v-5.3c0-1.3 0-2.9-1.8-2.9s-2 1.4-2 2.8V19h-3V9h2.9v1.4h.04a3.2 3.2 0 0 1 2.9-1.6c3 0 3.6 2 3.6 4.6V19Z',
  },
];
